import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { ACCESS_TOKEN_STORAGE_KEY, ID_TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '@okta/okta-auth-js';
import { firstValueFrom } from 'rxjs';
import { maintenanceMode } from '../interceptors/token-interceptor';
import { ConfigService } from './config.service';
import { NotificationsService } from './notifications.service';

@Injectable()
export class AuthService {

  publicPath: string;
  isAuthenticated = false;

  constructor(@Inject(OKTA_AUTH) public oktaAuth: OktaAuth, private readonly configService: ConfigService,
    private readonly httpClient: HttpClient, private readonly router: Router, private readonly notificationsService: NotificationsService,
    private readonly idle: Idle, private readonly keepalive: Keepalive) {
    this.publicPath = this.configService.appConfig.api.publicPath;
    if (this.checkSession()) {
      this.isAuthenticated = true;
    }
  }

  ttoc(): Promise<any> {

    const accessToken = this.oktaAuth.tokenManager.getTokensSync()?.accessToken?.accessToken;
    return firstValueFrom(this.httpClient.post(this.publicPath + '/ttoc', {
      access_token: accessToken
    }, {
      headers: {
        "x-portal-id": ""
      }
    })).then(() => {
      this.isAuthenticated = true;
    })
      .finally(() => {
        // removes all tokens (access, ID, and refresh tokens)
        this.oktaAuth.tokenManager.remove(ACCESS_TOKEN_STORAGE_KEY);
        this.oktaAuth.tokenManager.remove(ID_TOKEN_STORAGE_KEY);
        this.oktaAuth.tokenManager.remove(REFRESH_TOKEN_STORAGE_KEY);
      })
      .catch(e => {
        this.signOut();  // something wrong in network - signout
      });
  }

  public async signOut(): Promise<void> {

    await this.oktaAuth.closeSession().finally(() => {
      // Note: revokeAccessToken will be handled by the backend in the below call /signout
      firstValueFrom(this.httpClient.post(this.publicPath + '/signout', null, {
        headers: {
          "x-portal-id": ""
        }
      })).finally(() => {

        this.notificationsService.clearAll(); // clear all modal when timeout
        this.keepalive.stop();
        this.idle.stop();
        this.oktaAuth.tokenManager.clear();

        if (maintenanceMode) {
          window.location.href = window.location.origin + '/assets/html/maintenance.html';
        }
        else {
          this.router.navigate(['/']);
        }
      });
    });
  }

  routeToDefault(): void {
    // Handle external links routes to login component
    let originUrl = sessionStorage.getItem('originUrl');
    if (originUrl) {
      if (originUrl.indexOf('/notify/rid') > 0) {
        this.router.navigate(['/portal/results']).then(() => {
          this.router.navigate(['/portal/results/search-results/' + originUrl.substring(originUrl.indexOf('rid') + 3)]);
        });
      } else if (originUrl.indexOf('/notify/qid') > 0) {
        this.router.navigate(['/portal/results']).then(() => {
          this.router.navigate(['/portal/results/search/' + originUrl.substring(originUrl.indexOf('qid') + 3)]);
        });
      } else if (originUrl.indexOf('/userpref') > 0) {
        this.router.navigate(['/portal/preferences']).then(() => {
          this.router.navigate(['/portal/preferences/user-preferences']);
        });
      } else if (originUrl.indexOf('/portal') > 0) {
        this.router.navigate([originUrl.substring(originUrl.indexOf('/portal'))]);
      } else {
        this.router.navigate([this.configService.appConfig.application.defaultRoute]);
      }
      if (this.isAuthenticated) sessionStorage.removeItem('originUrl');
    } else {
      this.router.navigate([this.configService.appConfig.application.defaultRoute]);
    }
  }

  async checkSession(): Promise<boolean> {
    const isSession = await this.oktaAuth.session.exists().catch(e => {
      return false;
    });
    return isSession.valueOf();
  }
}